import React, { useState } from 'react';
import {
  Mail,
  Lock,
  User,
  Upload,
  AlertCircle,
  Loader2,
  LogIn,
  UserPlus,
} from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/Components/ui/dialog';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/Components/ui/tabs';

import { loginCandidate, signupCandidate } from './publicJobsApi';

/**
 * CandidateLoginDialog
 *
 * Two tabs in one popup:
 *   • Login   → POST /api/user-login        (existing endpoint)
 *   • Sign Up → POST /api/signup-apply      (existing endpoint)
 *
 * On successful auth we call `onSuccess()` so the parent can advance to
 * the next step (typically opening the Apply form). No new backend code
 * is introduced here.
 */
export function CandidateLoginDialog({ onClose, onSuccess }) {
  const [tab, setTab] = useState('login');
  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Welcome to SearchJob.ai</DialogTitle>
          <DialogDescription>
            Login to apply for this role, or create an account in seconds.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login" className="gap-1.5">
              <LogIn className="h-3.5 w-3.5" />
              Login
            </TabsTrigger>
            <TabsTrigger value="signup" className="gap-1.5">
              <UserPlus className="h-3.5 w-3.5" />
              Sign Up
            </TabsTrigger>
          </TabsList>

          <TabsContent value="login" className="mt-4">
            <LoginForm
              onSuccess={onSuccess}
              switchToSignup={() => setTab('signup')}
            />
          </TabsContent>
          <TabsContent value="signup" className="mt-4">
            <SignupForm
              onSuccess={onSuccess}
              switchToLogin={() => setTab('login')}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Login tab
// ---------------------------------------------------------------------------

function LoginForm({ onSuccess, switchToSignup }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Email and password are required.');
      return;
    }
    setBusy(true);
    try {
      await loginCandidate({ email, password });
      onSuccess?.();
    } catch (err) {
      setError(
        (err.body && err.body.message) ||
          err.message ||
          'Login failed. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor="login-email">Email</Label>
        <div className="relative">
          <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="pl-9"
            disabled={busy}
            autoFocus
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="login-password">Password</Label>
        <div className="relative">
          <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="pl-9"
            disabled={busy}
          />
        </div>
      </div>

      {error && <ErrorBanner message={error} />}

      <DialogFooter className="flex-col gap-2 sm:flex-col sm:gap-2">
        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Signing in…
            </>
          ) : (
            'Login'
          )}
        </Button>
        <p className="text-center text-xs text-muted-foreground">
          New here?{' '}
          <button
            type="button"
            className="font-medium text-primary hover:underline"
            onClick={switchToSignup}
          >
            Create an account
          </button>
        </p>
      </DialogFooter>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Signup tab — wraps `/api/signup-apply` (multipart with optional resume)
// ---------------------------------------------------------------------------

function SignupForm({ onSuccess, switchToLogin }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resume, setResume] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim() || name.trim().length < 2) {
      setError('Please enter your full name.');
      return;
    }
    if (!email || !password) {
      setError('Email and password are required.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setBusy(true);
    try {
      // Sign up first — backend creates the account but does NOT log in.
      await signupCandidate({ name, email, password, resume });
      // Then log in so the apply step has a valid session cookie.
      await loginCandidate({ email, password });
      onSuccess?.();
    } catch (err) {
      setError(
        (err.body && (err.body.error || err.body.message)) ||
          err.message ||
          'Signup failed. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor="signup-name">Full Name</Label>
        <div className="relative">
          <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="signup-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Aman Sharma"
            className="pl-9"
            disabled={busy}
            autoFocus
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="signup-email">Email</Label>
        <div className="relative">
          <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="signup-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="pl-9"
            disabled={busy}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="signup-password">Password</Label>
        <div className="relative">
          <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="signup-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 8 characters, letters + digits"
            className="pl-9"
            disabled={busy}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="signup-resume">Resume (optional)</Label>
        <div className="relative">
          <Upload className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="signup-resume"
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={(e) => setResume(e.target.files?.[0] || null)}
            className="pl-9"
            disabled={busy}
          />
        </div>
        <p className="text-[10px] text-muted-foreground">
          PDF, DOC or DOCX. You can upload later from your profile.
        </p>
      </div>

      {error && <ErrorBanner message={error} />}

      <DialogFooter className="flex-col gap-2 sm:flex-col sm:gap-2">
        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating account…
            </>
          ) : (
            'Create account & continue'
          )}
        </Button>
        <p className="text-center text-xs text-muted-foreground">
          Already have an account?{' '}
          <button
            type="button"
            className="font-medium text-primary hover:underline"
            onClick={switchToLogin}
          >
            Login
          </button>
        </p>
      </DialogFooter>
    </form>
  );
}

function ErrorBanner({ message }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-2.5 text-sm text-destructive">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{message}</span>
    </div>
  );
}
